import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  Input,
  Textarea,
  Select,
  HStack,
  Text,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Stack,
  Heading,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  FormHelperText,
} from "@chakra-ui/react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2 } from "lucide-react";
import { Select as CRSelect } from "chakra-react-select";
import MediaUpload from "features/jobs/media-upload";
import { EducationLevel } from "__generated__/graphql";
import { cleanPhoneNumber, debounce } from "../../../../lib/utils";
import countries from "../../../../assets/countries.json";
import { useAuth0 } from "@auth0/auth0-react";
import { useJobApplicantProfile } from "hooks/useJobApplicantProfile";
import { useProProfile } from "hooks/useProProfile";
import { gql } from "__generated__";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  MY_JOB_APPLICATIONS,
  GET_MY_PORTFOLIO_FOR_JOB_APPLICATION_PREFILL,
  DOES_APPLICANT_EXIST,
  DOES_PRO_EXIST,
} from "features/jobs/queries";
import { isEqual } from "lodash";
import { logEvent } from "helpers/analytics";
import {
  JobApplicationFormData as FormData,
  jobApplicationFormSchema as formSchema,
  JobApplicationFormProps,
  tertiaryQualifications,
  educationLevels,
} from "features/jobs/constants";
import { convertDateForInput, convertMedia } from "features/jobs/utils";

const UPDATE_GUEST_APPLICANT_USER_ID = gql(`
  mutation UpdateGuestApplicantUserId($email: String!) {
    updateGuestApplicantUserId(email: $email)
  }
`);

export default function JobApplicationForm({
  onSubmit,
  onCancel,
  isSubmitting,
  jobId,
}: JobApplicationFormProps) {
  const {
    user,
    isLoading: authIsLoading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const [checkingForToken, setCheckingForToken] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState("NG");
  const [phoneNumberInput, setPhoneNumberInput] = useState("");

  // Track if we've already attempted to update guest applicant user ID
  const hasAttemptedUserIdUpdate = useRef(false);

  // Store original form data after prefilling
  const [originalFormPrefilledData, setOriginalFormPrefilledData] =
    useState<FormData | null>(null);

  const {
    applicantProfile,
    loading: applicantProfileLoading,
    refetch: refetchApplicantProfile,
  } = useJobApplicantProfile();
  const { profile: proProfile, loading: proProfileLoading } = useProProfile();

  // Email verification state - start as 'new-user' if user is authenticated and has profile
  const [emailStep, setEmailStep] = useState<
    "input" | "existing-user" | "new-user"
  >(
    isAuthenticated && (applicantProfile || proProfile)
      ? "existing-user"
      : "input"
  );
  const [emailError, setEmailError] = useState("");

  const callingCodeOptions = Object.values(countries).map((country) => ({
    label: country.calling_code,
    value: country.alpha2,
  }));

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      educationItems: [],
      employmentHistoryItems: [],
      projectHistoryItems: [],
      certificationItems: [],
      email: "",
    },
  });

  // Watch email field for changes
  const emailValue = watch("email");

  //console.log({ errors });

  // Query to check if applicant exists
  const [checkApplicantExists, { loading: checkingApplicantExists }] =
    useLazyQuery(DOES_APPLICANT_EXIST, {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data.doesApplicantExistWithEmail) {
          // Applicant exists, trigger Auth0 login with login hint
          loginWithRedirect({
            redirectUri: `${window.location.origin}/jobs/apply?job-id=${jobId}`,
            login_hint: emailValue.toLowerCase(),
          });

          setEmailStep("existing-user");
        } else {
          // Applicant doesn't exist, check if pro exists
          checkProExists({
            variables: { email: emailValue.toLowerCase() },
          });
        }
      },
      onError: (error) => {
        setEmailError("Failed to verify email. Please try again.");
        setEmailStep("input");
      },
    });

  // Query to check if pro exists (called after applicant check)
  const [checkProExists, { loading: checkingProExists }] = useLazyQuery(
    DOES_PRO_EXIST,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data.doesProExistWithEmail) {
          // Pro exists, trigger Auth0 login with login hint
          loginWithRedirect({
            redirectUri: `${window.location.origin}/jobs/apply?job-id=${jobId}`,
            login_hint: emailValue.toLowerCase(),
          });

          setEmailStep("existing-user");
        } else {
          // Neither applicant nor pro exists, allow form completion
          setEmailStep("new-user");
        }
      },
      onError: (error) => {
        setEmailError("Failed to verify email. Please try again.");
        setEmailStep("input");
      },
    }
  );

  const [
    getLastJobApplication,
    { data: lastJobApplicationData, loading: loadingLastApplication },
  ] = useLazyQuery(MY_JOB_APPLICATIONS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data.myJobApplications && data.myJobApplications.length > 0) {
        populateFormFromLastApplication(data.myJobApplications[0]);
      } else {
        getPortfolioData();
      }
    },
    onError: (error) => {
      console.error("Error fetching last job application:", error);
      getPortfolioData();
    },
  });

  const [getPortfolioData, { data: portfolioData, loading: loadingPortfolio }] =
    useLazyQuery(GET_MY_PORTFOLIO_FOR_JOB_APPLICATION_PREFILL, {
      onCompleted: (data) => {
        if (data.myPortfolio) {
          populateFormFromPortfolio(data.myPortfolio);
        }
      },
      onError: (error) => {
        console.error("Error fetching portfolio data:", error);
      },
    });

  // State to track if we're waiting for profile refetch after user ID update
  const [waitingForProfileRefetch, setWaitingForProfileRefetch] =
    useState(false);

  // Mutation to update guest applicant user ID
  const [
    updateGuestApplicantUserId,
    { loading: updatingGuestApplicantUserId },
  ] = useMutation(UPDATE_GUEST_APPLICANT_USER_ID, {
    onCompleted: async (data) => {
      if (data.updateGuestApplicantUserId) {
        logEvent("guest_applicant_user_id_updated", {
          jobAdId: jobId,
          userId: user?.id,
          email: user?.email,
        });

        // Refetch applicant profile after updating user ID
        // This ensures we get the updated profile with the correct user ID
        try {
          await refetchApplicantProfile();
        } catch (error) {
          console.error("Error refetching applicant profile:", error);
        } finally {
          // Clear waiting state after refetch completes (success or failure)
          setWaitingForProfileRefetch(false);
        }
      } else {
        // If the mutation didn't succeed, clear the waiting state
        setWaitingForProfileRefetch(false);
      }
    },
    onError: (error) => {
      console.error("Error updating guest applicant user ID:", error);
      setWaitingForProfileRefetch(false);
    },
  });

  // Helper functions to check if applicant data exists (with pro profile as backup)
  const hasApplicantData = (field: string) => {
    switch (field) {
      case "name":
        return !!(
          (applicantProfile?.firstName && applicantProfile?.lastName) ||
          (proProfile?.firstName && proProfile?.lastName)
        );
      case "email":
        return !!(applicantProfile?.email || proProfile?.email);
      case "phone":
        return !!(applicantProfile?.phoneNumber || proProfile?.phoneNumber);
      case "yearsOfExperience":
        return (
          (applicantProfile?.yearsOfExperience !== null &&
            applicantProfile?.yearsOfExperience !== undefined) ||
          (proProfile?.yearsOfExperience !== null &&
            proProfile?.yearsOfExperience !== undefined)
        );
      default:
        return false;
    }
  };

  // Check if all personal info exists to hide the entire personal info section
  const hasAllPersonalInfo = () => {
    return (
      hasApplicantData("name") &&
      hasApplicantData("email") &&
      hasApplicantData("phone") &&
      hasApplicantData("yearsOfExperience")
    );
  };

  /**
   * Function to populate form from last job application
   * @param lastApplication - The last job application data to populate the form from
   */
  const populateFormFromLastApplication = (
    lastApplication: NonNullable<
      typeof lastJobApplicationData
    >["myJobApplications"][0]
  ) => {
    if (!lastApplication) return;

    // Populate education items
    if (lastApplication.education && lastApplication.education.length > 0) {
      const educationItems = lastApplication.education.map((education) => ({
        schoolName: education.schoolName,
        fieldOfStudy: education.fieldOfStudy || "",
        qualification: education.qualification,
      }));
      setValue("educationItems", educationItems);
    }

    // Populate employment history items
    if (
      lastApplication.employmentHistory &&
      lastApplication.employmentHistory.length > 0
    ) {
      const employmentItems = lastApplication.employmentHistory.map(
        (employment) => ({
          companyName: employment.companyName,
          jobTitle: employment.jobTitle,
          description: employment.description,
          startedAt: convertDateForInput(employment.startedAt),
          endedAt: convertDateForInput(employment.endedAt),
          media: convertMedia(employment.media),
        })
      );
      setValue("employmentHistoryItems", employmentItems);
    }

    // Populate project history items
    if (
      lastApplication.projectHistory &&
      lastApplication.projectHistory.length > 0
    ) {
      const projectItems = lastApplication.projectHistory.map((project) => ({
        name: project.name,
        description: project.description,
        startedAt: convertDateForInput(project.startedAt),
        endedAt: convertDateForInput(project.endedAt),
        media: convertMedia(project.media),
      }));
      setValue("projectHistoryItems", projectItems);
    }

    // Populate certifications
    if (
      lastApplication.certifications &&
      lastApplication.certifications.length > 0
    ) {
      const certificationItems = lastApplication.certifications.map(
        (certification) => ({
          name: certification.name,
          issuerCertificationId: certification.issuerCertificationId || "",
          issuedBy: certification.issuedBy,
          issuedAt: convertDateForInput(certification.issuedAt),
          url: certification.url || "",
        })
      );
      setValue("certificationItems", certificationItems);
    }

    logEvent("application_form_prefilled", {
      jobAdId: jobId,
      userId: user?.id,
      source: "last_job_application",
    });

    // set the original form prefilled data
    const currentFormData = normalizeFormData(watch());
    setOriginalFormPrefilledData(structuredClone(currentFormData));
  };

  /**
   * Function to populate form from portfolio data
   * @param portfolio - The portfolio data to populate the form from
   */
  const populateFormFromPortfolio = (
    portfolio: NonNullable<typeof portfolioData>["myPortfolio"]
  ) => {
    if (!portfolio) return;

    // Check if there's at least one work history or employment history item
    const hasWorkHistory =
      portfolio.workHistory && portfolio.workHistory.length > 0;
    const hasEmploymentHistory =
      portfolio.employmentHistory && portfolio.employmentHistory.length > 0;

    if (!hasWorkHistory && !hasEmploymentHistory) {
      return; // Don't prefill if no work/employment history
    }

    // Populate education items from portfolio
    if (portfolio.educationEntries && portfolio.educationEntries.length > 0) {
      const educationItems = portfolio.educationEntries.map((education) => ({
        schoolName: education.schoolName,
        fieldOfStudy: education.fieldOfStudy || "",
        qualification: education.qualification,
      }));
      setValue("educationItems", educationItems);
    }

    // Populate employment history items from portfolio
    if (hasEmploymentHistory) {
      const employmentItems = portfolio.employmentHistory.map((employment) => ({
        companyName: employment.companyName,
        jobTitle: employment.jobTitle,
        description: employment.description,
        startedAt: convertDateForInput(employment.startedAt),
        endedAt: convertDateForInput(employment.endedAt),
        media: convertMedia(
          employment.images
            // filter out images that don't have a key, mime type, original url, and thumbnail url
            .filter(
              (image) =>
                image.key &&
                image.mimeType &&
                image.originalUrl &&
                image.thumbnailUrl
            )
            .map((image) => ({
              key: image.key!,
              mimeType: image.mimeType,
              originalUrl: image.originalUrl!,
              thumbnailUrl: image.thumbnailUrl!,
            }))
        ),
      }));
      setValue("employmentHistoryItems", employmentItems);
    }

    // Populate project history items from portfolio work history
    if (hasWorkHistory) {
      const projectItems = portfolio.workHistory.map((work) => ({
        name: work.name,
        description: work.description,
        startedAt: convertDateForInput(work.startedAt),
        endedAt: convertDateForInput(work.endedAt),
        media: convertMedia(
          work.images
            // filter out images that don't have a key, mime type, original url, and thumbnail url
            .filter(
              (image) =>
                image.key &&
                image.mimeType &&
                image.originalUrl &&
                image.thumbnailUrl
            )
            .map((image) => ({
              key: image.key!,
              mimeType: image.mimeType,
              originalUrl: image.originalUrl!,
              thumbnailUrl: image.thumbnailUrl!,
            }))
        ),
      }));
      setValue("projectHistoryItems", projectItems);
    }

    // Populate certifications from portfolio
    if (portfolio.certifications && portfolio.certifications.length > 0) {
      const certificationItems = portfolio.certifications.map(
        (certification) => ({
          name: certification.name,
          issuerCertificationId: certification.issuerCertificationId || "",
          issuedBy: certification.issuedBy,
          issuedAt: convertDateForInput(certification.issuedAt),
          url: certification.url || "",
        })
      );
      setValue("certificationItems", certificationItems);
    }

    logEvent("application_form_prefilled", {
      jobAdId: jobId,
      userId: user?.id,
      source: "portfolio",
    });

    // set the original form prefilled data
    const currentFormData = normalizeFormData(watch());
    setOriginalFormPrefilledData(structuredClone(currentFormData));
  };

  // Extract phone number without country code for display
  const extractPhoneNumber = (fullPhoneNumber: string) => {
    if (!fullPhoneNumber) return "";

    // Find the country that matches the phone number
    const matchingCountry = Object.values(countries).find((country) =>
      fullPhoneNumber.startsWith(country.calling_code)
    );

    if (matchingCountry) {
      return fullPhoneNumber.substring(matchingCountry.calling_code.length);
    }

    if (fullPhoneNumber.startsWith("234")) {
      return fullPhoneNumber.substring(3);
    }

    if (fullPhoneNumber.startsWith("0")) {
      return fullPhoneNumber.substring(1);
    }

    return fullPhoneNumber;
  };

  // Get country code from phone number
  const getCountryFromPhone = (fullPhoneNumber: string) => {
    if (!fullPhoneNumber) return "NG";

    const matchingCountry = Object.values(countries).find((country) =>
      fullPhoneNumber.startsWith(country.calling_code)
    );

    return matchingCountry?.alpha2 || "NG";
  };

  /**
   * Function to normalize form data by converting undefined to empty strings
   * this is to avoid misreading data when comparing the original form prefilled data with the current form data
   * @param data - The form data to normalize
   * @returns The normalized form data
   */
  const normalizeFormData = useCallback((data: FormData): FormData => {
    return {
      ...data,
      educationItems: data.educationItems.map((item) => ({
        ...item,
        fieldOfStudy: item.fieldOfStudy || "",
      })),
      employmentHistoryItems: data.employmentHistoryItems.map((item) => ({
        ...item,
        endedAt: item.endedAt || "",
      })),
      projectHistoryItems: data.projectHistoryItems.map((item) => ({
        ...item,
        startedAt: item.startedAt || "",
      })),
      certificationItems: data.certificationItems.map((item) => ({
        ...item,
        issuerCertificationId: item.issuerCertificationId || "",
        url: item.url || "",
      })),
    };
  }, []);

  // Function to check if prefilled form has been modified
  const hasPrefilledFormChanged = useCallback(() => {
    if (!originalFormPrefilledData) return false;
    const currentData = normalizeFormData(watch());
    const originalData = normalizeFormData(originalFormPrefilledData);
    return !isEqual(originalData, currentData);
  }, [originalFormPrefilledData, watch, normalizeFormData]);

  const debouncedLogEvent = useCallback(
    debounce((event: string, data: Record<string, any>) => {
      logEvent(event, data);
    }, 1000),
    []
  );

  useEffect(() => {
    getAccessTokenSilently()
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setCheckingForToken(false);
      });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently();
    }
  }, [isAuthenticated]);

  // Update email step when authentication state or profiles change
  useEffect(() => {
    // Early return if not authenticated
    if (!isAuthenticated) {
      setEmailStep("input");
      return;
    }

    // Fire update applicant with user id in case user is logged in as a guest applicant
    // Only do this once when user first authenticates and we have their email
    if (
      user?.email &&
      !updatingGuestApplicantUserId &&
      !hasAttemptedUserIdUpdate.current
    ) {
      hasAttemptedUserIdUpdate.current = true;
      // Set waiting state immediately before triggering the mutation
      setWaitingForProfileRefetch(true);
      updateGuestApplicantUserId({
        variables: { email: user.email },
      });
    }

    // Wait for all loading states to complete before making decisions
    if (
      proProfileLoading ||
      applicantProfileLoading ||
      updatingGuestApplicantUserId ||
      waitingForProfileRefetch
    ) {
      return;
    }

    // If user has either profile, set to existing user
    if (proProfile || applicantProfile) {
      setEmailStep("existing-user");
      const email = user?.email || "";
      if (email) {
        setValue("email", email);
      }
    } else {
      // User is authenticated but has no profiles - log them out so they can apply as a first time guest applicant
      logout({
        returnTo: `${window.location.origin}/jobs/apply?job-id=${jobId}`,
      });
    }
  }, [
    isAuthenticated,
    applicantProfile,
    proProfile,
    user,
    proProfileLoading,
    applicantProfileLoading,
    updatingGuestApplicantUserId,
    waitingForProfileRefetch,
  ]);

  // Populate form with profile data (applicant first, then pro as backup)
  useEffect(() => {
    if (applicantProfile || proProfile) {
      // Set name fields (applicant first, then pro as backup)
      if (hasApplicantData("name")) {
        const firstName = applicantProfile?.firstName || proProfile?.firstName;
        const lastName = applicantProfile?.lastName || proProfile?.lastName;
        if (firstName) setValue("firstName", firstName);
        if (lastName) setValue("lastName", lastName);
      }

      // Set email (applicant first, then pro as backup)
      if (hasApplicantData("email")) {
        const email = applicantProfile?.email || proProfile?.email;
        if (email) setValue("email", email);
      }

      // Set phone number (applicant first, then pro as backup)
      if (hasApplicantData("phone")) {
        const phoneNumber = (
          applicantProfile?.phoneNumber || proProfile?.phoneNumber
        )?.trim();

        if (phoneNumber) {
          const countryCode = getCountryFromPhone(phoneNumber);
          const phoneOnly = extractPhoneNumber(phoneNumber);

          setSelectedCountry(countryCode);
          setPhoneNumberInput(phoneOnly);

          const dialingCode =
            countries[countryCode as keyof typeof countries].calling_code;
          setValue("phoneNumber", `${dialingCode}${phoneOnly}`);
        }
      }

      // Set years of experience (applicant first, then pro as backup)
      if (hasApplicantData("yearsOfExperience")) {
        const yearsOfExperience =
          applicantProfile?.yearsOfExperience ?? proProfile?.yearsOfExperience;
        if (yearsOfExperience !== null && yearsOfExperience !== undefined) {
          setValue("yearsOfExperience", yearsOfExperience as number);
        }
      }
    }
  }, [applicantProfile, proProfile, setValue]);

  // Fetch last job application when user is authenticated
  useEffect(() => {
    if (
      isAuthenticated &&
      !applicantProfileLoading &&
      !proProfileLoading &&
      !updatingGuestApplicantUserId
    ) {
      getLastJobApplication({
        variables: {
          limit: 1,
        },
      });
    }
  }, [
    isAuthenticated,
    applicantProfileLoading,
    proProfileLoading,
    getLastJobApplication,
    updatingGuestApplicantUserId,
  ]);

  const {
    fields: educationFields,
    append: appendEducation,
    remove: removeEducation,
  } = useFieldArray({
    control,
    name: "educationItems",
  });

  const {
    fields: projectFields,
    append: appendProject,
    remove: removeProject,
  } = useFieldArray({
    control,
    name: "projectHistoryItems",
  });

  const {
    fields: employmentFields,
    append: appendEmployment,
    remove: removeEmployment,
  } = useFieldArray({
    control,
    name: "employmentHistoryItems",
  });

  const {
    fields: certificationFields,
    append: appendCertification,
    remove: removeCertification,
  } = useFieldArray({
    control,
    name: "certificationItems",
  });

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setPhoneNumberInput(inputValue);

    // Clean the phone number and combine with country code
    const cleaned = cleanPhoneNumber(inputValue);
    const fullPhoneNumber = cleaned
      ? `${
          countries[selectedCountry as keyof typeof countries].calling_code
        }${cleaned}`
      : countries[selectedCountry as keyof typeof countries].calling_code;

    // Update the form value
    setValue("phoneNumber", fullPhoneNumber);

    // Log event when user actually types in phone number
    if (inputValue.trim()) {
      debouncedLogEvent("phoneNumber_changed", {
        jobAdId: jobId,
        userId: user?.id,
      });
    }
  };

  const handleCountryChange = (
    option: { label: string; value: string } | null
  ) => {
    if (option) {
      const country = countries[option.value as keyof typeof countries];
      if (country) {
        setSelectedCountry(country.alpha2);

        // Update phone number with new country code if there's a phone number entered
        if (phoneNumberInput) {
          const cleaned = cleanPhoneNumber(phoneNumberInput);
          const fullPhoneNumber = cleaned
            ? `${country.calling_code}${cleaned}`
            : country.calling_code;
          setValue("phoneNumber", fullPhoneNumber);
        }
      }
    }
  };

  const customSelectStyles = {
    control: (baseStyles: Record<string, unknown>) => ({
      ...baseStyles,
      height: {
        base: "40px",
        lg: "64px",
      },
      borderRadius: 0,
      border: "1px solid #D7D7D7",
      bgColor: "transparent",
      borderColor: "#D7D7D7",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#D7D7D7",
      },
    }),
  };

  const convertToISOString = (dateString: string): string => {
    if (!dateString) return dateString;
    // Create a date object and convert to ISO string
    const date = new Date(dateString);
    return date.toISOString();
  };

  const onFormSubmit = (data: FormData) => {
    // Transform the data to match the GraphQL mutation structure
    const transformedData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      yearsOfExperience: data.yearsOfExperience,
      educationItems: data.educationItems,
      employmentHistoryItems: data.employmentHistoryItems.map((item) => ({
        ...item,
        startedAt: convertToISOString(item.startedAt),
        endedAt: !!item.endedAt ? convertToISOString(item.endedAt) : undefined,
      })),
      projectHistoryItems: data.projectHistoryItems.map((item) => ({
        ...item,
        startedAt: !!item.startedAt
          ? convertToISOString(item.startedAt)
          : undefined,
        endedAt: convertToISOString(item.endedAt),
      })),
      certificationItems: data.certificationItems.map((item) => ({
        ...item,
        issuedAt: convertToISOString(item.issuedAt),
      })),
    };

    onSubmit(transformedData);

    if (hasPrefilledFormChanged()) {
      logEvent("prefilled_form_modified", {
        jobAdId: jobId,
        userId: user?.id,
        prefilledFrom:
          lastJobApplicationData?.myJobApplications &&
          lastJobApplicationData.myJobApplications.length > 0
            ? "job_application"
            : "portfolio",
      });
    } else if (
      lastJobApplicationData?.myJobApplications &&
      lastJobApplicationData.myJobApplications.length > 0 &&
      !hasPrefilledFormChanged()
    ) {
      logEvent("prefilled_form_not_modified", {
        jobAdId: jobId,
        userId: user?.id,
        prefilledFrom: "job_application",
      });
    } else if (portfolioData?.myPortfolio && !hasPrefilledFormChanged()) {
      logEvent("prefilled_form_not_modified", {
        jobAdId: jobId,
        userId: user?.id,
        prefilledFrom: "portfolio",
      });
    }
  };

  const handleEmailSubmit = () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setEmailError("");
    logEvent("email_submitted", {
      jobAdId: jobId,
      userId: user?.id,
    });
    checkApplicantExists({
      variables: { email: emailValue.toLowerCase() },
    });
  };

  const resetEmailStep = () => {
    setEmailStep("input");
    setValue("email", "");
    setEmailError("");
    logEvent("email_reset", {
      jobAdId: jobId,
      userId: user?.id,
    });
  };

  if (
    authIsLoading ||
    checkingForToken ||
    applicantProfileLoading ||
    proProfileLoading ||
    loadingLastApplication ||
    updatingGuestApplicantUserId ||
    loadingPortfolio
  ) {
    return <div>Loading...</div>;
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onFormSubmit)} maxW="4xl" mx="auto">
      <Stack spacing={8}>
        {/* Email First Step - Only show if user is not authenticated */}
        {!hasApplicantData("email") && (
          <Box>
            <Heading
              as="h2"
              size="lg"
              fontWeight="medium"
              color="#121212"
              mb={6}
            >
              {`Let's start with your email`}
            </Heading>

            {emailStep === "input" && (
              <Stack spacing={4}>
                <FormControl isInvalid={!!errors.email || !!emailError}>
                  <div className="flex flex-col gap-4">
                    <label
                      htmlFor="email-first"
                      className="text-md lg:text-xl text-[#212121] font-medium"
                    >
                      Email address
                    </label>
                    <Input
                      id="email-first"
                      type="email"
                      {...register("email")}
                      onChange={(e) => {
                        register("email").onChange(e);
                        if (e.target.value.trim()) {
                          debouncedLogEvent("email_changed", {
                            jobAdId: jobId,
                            userId: user?.id,
                          });
                        }
                      }}
                      placeholder="adedeji@gmail.com"
                      variant="outline"
                      rounded={0}
                      border="1px solid #D7D7D7"
                      py={{ base: 5, lg: 8 }}
                      px={{ base: 3, lg: 5 }}
                      size="lg"
                    />
                    {isAuthenticated && (
                      <FormHelperText>
                        You are currently logged in as {user?.email}. Want to
                        use a different email?{" "}
                        <Button
                          variant="link"
                          size="sm"
                          colorScheme="flatteredFlamingo"
                          onClick={() =>
                            logout({
                              returnTo:
                                window.location.origin +
                                "/jobs/apply?job-id=" +
                                jobId,
                            })
                          }
                        >
                          Log out
                        </Button>
                      </FormHelperText>
                    )}
                    {(errors.email?.message || emailError) && (
                      <FormErrorMessage>
                        {errors.email?.message || emailError}
                      </FormErrorMessage>
                    )}
                  </div>
                </FormControl>

                <Button
                  onClick={handleEmailSubmit}
                  colorScheme="flatteredFlamingo"
                  size="lg"
                  rounded={0}
                  px={8}
                  py={6}
                  disabled={!emailValue.trim()}
                >
                  Continue
                </Button>
              </Stack>
            )}

            {(checkingApplicantExists || checkingProExists) && (
              <Alert status="info" rounded={0}>
                <Spinner size="sm" mr={3} />
                <AlertTitle>Checking your email...</AlertTitle>
                <AlertDescription>
                  {`We're verifying if you have an existing account.`}
                </AlertDescription>
              </Alert>
            )}

            {emailStep === "new-user" && (
              <Alert status="success" rounded={0}>
                <AlertIcon />
                <Box>
                  <AlertTitle>{`Great! Let's create your application.`}</AlertTitle>
                  <AlertDescription>
                    You can now complete the application form below.
                    {!isAuthenticated && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={resetEmailStep}
                        ml={2}
                        color="blue.500"
                      >
                        Change email
                      </Button>
                    )}
                  </AlertDescription>
                </Box>
              </Alert>
            )}
          </Box>
        )}

        {emailStep === "existing-user" && isAuthenticated && (
          <div className="text-[#153243] text-base md:text-lg leading-7 font-medium font-[Inter] bg-gradient-to-r from-flatteredFlamingo-400/5 to-flatteredFlamingo-400/10 border border-flatteredFlamingo-300/20 p-6 my-8">
            You are currently logged in as {user?.email}.
          </div>
        )}

        {/* Rest of the form - blurred until email is verified OR always visible for authenticated users */}
        <Box
          opacity={
            emailStep === "existing-user" || emailStep === "new-user" ? 1 : 0.4
          }
          filter={
            emailStep === "existing-user" || emailStep === "new-user"
              ? "none"
              : "blur(2px)"
          }
          pointerEvents={
            emailStep === "existing-user" || emailStep === "new-user"
              ? "auto"
              : "none"
          }
          transition="all 0.3s ease"
        >
          {/* Personal Information Section - Only show if not all data exists */}
          {!hasAllPersonalInfo() && (
            <Box mb={8}>
              <Heading
                as="h2"
                size="lg"
                fontWeight="medium"
                color="#121212"
                mb={6}
              >
                Personal information
              </Heading>

              <Stack spacing={6}>
                {/* Name fields - only show if name data doesn't exist */}
                {!hasApplicantData("name") && (
                  <HStack spacing={4}>
                    <FormControl isInvalid={!!errors.firstName}>
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor="firstName"
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          First name
                        </label>
                        <Input
                          {...register("firstName")}
                          placeholder="Adedeji"
                          variant="outline"
                          rounded={0}
                          border="1px solid #D7D7D7"
                          py={{ base: 5, lg: 8 }}
                          px={{ base: 3, lg: 5 }}
                          onChange={(e) => {
                            register("firstName").onChange(e);
                            if (e.target.value.trim()) {
                              debouncedLogEvent("firstName_changed", {
                                jobAdId: jobId,
                                userId: user?.id,
                              });
                            }
                          }}
                        />
                        <FormErrorMessage>
                          {errors.firstName?.message}
                        </FormErrorMessage>
                      </div>
                    </FormControl>

                    <FormControl isInvalid={!!errors.lastName}>
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor="lastName"
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          Last name
                        </label>
                        <Input
                          {...register("lastName")}
                          placeholder="Olisa"
                          variant="outline"
                          rounded={0}
                          border="1px solid #D7D7D7"
                          py={{ base: 5, lg: 8 }}
                          px={{ base: 3, lg: 5 }}
                          onChange={(e) => {
                            register("lastName").onChange(e);
                            if (e.target.value.trim()) {
                              debouncedLogEvent("lastName_changed", {
                                jobAdId: jobId,
                                userId: user?.id,
                              });
                            }
                          }}
                        />
                        <FormErrorMessage>
                          {errors.lastName?.message}
                        </FormErrorMessage>
                      </div>
                    </FormControl>
                  </HStack>
                )}

                <Stack spacing={4} direction={{ base: "column", lg: "row" }}>
                  {/* Email field - show as read-only since it's already captured */}
                  {!hasApplicantData("email") && (
                    <FormControl>
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor="email"
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          Email address
                        </label>
                        <Input
                          value={emailValue}
                          type="email"
                          placeholder="adedeji@gmail.com"
                          variant="outline"
                          rounded={0}
                          border="1px solid #D7D7D7"
                          py={{ base: 5, lg: 8 }}
                          px={{ base: 3, lg: 5 }}
                          bg="gray.50"
                          readOnly
                        />
                      </div>
                    </FormControl>
                  )}

                  {/* Phone number field - only show if phone doesn't exist */}
                  {!hasApplicantData("phone") && (
                    <FormControl isInvalid={!!errors.phoneNumber}>
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor="phoneNumber"
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          Phone number
                        </label>
                        <HStack spacing={0}>
                          <Box width="120px" flexShrink={0}>
                            <CRSelect
                              value={callingCodeOptions.find(
                                (option) => option.value === selectedCountry
                              )}
                              options={callingCodeOptions}
                              onChange={handleCountryChange}
                              placeholder="Select"
                              isSearchable
                              chakraStyles={{
                                control: (provided) => ({
                                  ...provided,
                                  border: "1px solid #D7D7D7",
                                  borderColor: "#D7D7D7",
                                  bgColor: "transparent",
                                  borderRadius: 0,
                                  width: "120px",
                                  maxWidth: "120px",
                                  minHeight: { base: "37px", lg: "58px" },
                                  borderRight: "none",
                                  boxShadow: "none",
                                  "&:hover": {
                                    borderColor: "#D7D7D7",
                                  },
                                }),
                                menu: (provided) => ({
                                  ...provided,
                                  zIndex: 1000,
                                }),
                              }}
                            />
                          </Box>
                          <Input
                            value={phoneNumberInput}
                            onChange={handlePhoneNumberChange}
                            placeholder="8060000000"
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            borderLeft="none"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            flex={1}
                          />
                        </HStack>
                        <FormErrorMessage>
                          {errors.phoneNumber?.message}
                        </FormErrorMessage>
                      </div>
                    </FormControl>
                  )}
                </Stack>
                {/* Years of experience field - only show if it doesn't exist */}
                {!hasApplicantData("yearsOfExperience") && (
                  <FormControl isInvalid={!!errors.yearsOfExperience}>
                    <div className="flex flex-col gap-4">
                      <label
                        htmlFor="yearsOfExperience"
                        className="text-md lg:text-xl text-[#212121] font-medium"
                      >
                        Years of experience
                      </label>
                      <Controller
                        name="yearsOfExperience"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            {...field}
                            min={0}
                            max={50}
                            rounded={0}
                            border="#D7D7D7"
                          >
                            <NumberInputField
                              placeholder="Enter years of experience"
                              rounded={0}
                              border="1px solid #D7D7D7"
                              py={{ base: 5, lg: 8 }}
                              px={{ base: 3, lg: 5 }}
                              onChange={(e) => {
                                if (e.target.value) {
                                  debouncedLogEvent(
                                    "yearsOfExperience_changed",
                                    {
                                      jobAdId: jobId,
                                      userId: user?.id,
                                    }
                                  );
                                }
                              }}
                            />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        )}
                      />
                      <FormErrorMessage>
                        {errors.yearsOfExperience?.message}
                      </FormErrorMessage>
                    </div>
                  </FormControl>
                )}
              </Stack>
            </Box>
          )}
          {/* Education Section */}
          <Box py={8} borderBottom="1px" borderColor="#E7E7E7">
            <HStack justify="space-between" mb={6}>
              <Heading
                as="h2"
                fontSize={{ base: "xl", lg: "2xl" }}
                fontWeight="medium"
                color="#121212"
              >
                Education
              </Heading>
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                size="md"
                variant="outline"
                rounded={0}
                colorScheme="prussianBlue"
                onClick={() => {
                  appendEducation({
                    schoolName: "",
                    fieldOfStudy: "",
                    qualification: EducationLevel.Ssce,
                  });
                  logEvent("education_added", {
                    jobAdId: jobId,
                    userId: user?.id,
                  });
                }}
              >
                Add
              </Button>
            </HStack>

            <Stack spacing={6}>
              {educationFields.map((field, index) => (
                <Box key={field.id} p={6} border="1px" borderColor="#C9C9C9">
                  <HStack justify="space-between" mb={4}>
                    <Text fontWeight="medium" fontSize="lg">
                      Education {index + 1}
                    </Text>
                    {educationFields.length > 1 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => {
                          removeEducation(index);
                          logEvent("education_removed", {
                            jobAdId: jobId,
                            userId: user?.id,
                          });
                        }}
                        leftIcon={<Trash2 className="h-4 w-4" />}
                      >
                        Remove
                      </Button>
                    )}
                  </HStack>

                  <Stack spacing={4}>
                    <FormControl
                      isInvalid={!!errors.educationItems?.[index]?.schoolName}
                    >
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor={`schoolName-${index}`}
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          School name
                        </label>
                        <Input
                          {...register(`educationItems.${index}.schoolName`)}
                          placeholder="Enter school name"
                          variant="outline"
                          rounded={0}
                          border="1px solid #D7D7D7"
                          py={{ base: 5, lg: 8 }}
                          px={{ base: 3, lg: 5 }}
                          bg="white"
                          onChange={(e) => {
                            register(
                              `educationItems.${index}.schoolName`
                            ).onChange(e);
                            if (e.target.value.trim()) {
                              debouncedLogEvent(
                                "education_schoolName_changed",
                                {
                                  jobAdId: jobId,
                                  userId: user?.id,
                                }
                              );
                            }
                          }}
                        />
                        <FormErrorMessage>
                          {errors.educationItems?.[index]?.schoolName?.message}
                        </FormErrorMessage>
                      </div>
                    </FormControl>

                    <FormControl
                      isInvalid={
                        !!errors.educationItems?.[index]?.qualification
                      }
                    >
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor={`qualification-${index}`}
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          Qualification
                        </label>
                        <Select
                          id={`qualification-${index}`}
                          {...register(`educationItems.${index}.qualification`)}
                          placeholder="Select qualification"
                          variant="outline"
                          rounded={0}
                          border="1px solid #D7D7D7"
                          size={{ base: "md", lg: "lg" }}
                          h={{ base: 10, lg: 16 }}
                          bg="white"
                          onChange={(e) => {
                            register(
                              `educationItems.${index}.qualification`
                            ).onChange(e);
                            if (e.target.value) {
                              debouncedLogEvent(
                                "education_qualification_changed",
                                {
                                  jobAdId: jobId,
                                  userId: user?.id,
                                }
                              );
                            }
                          }}
                        >
                          {educationLevels.map((level) => (
                            <option key={level.value} value={level.value}>
                              {level.label}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>
                          {
                            errors.educationItems?.[index]?.qualification
                              ?.message
                          }
                        </FormErrorMessage>
                      </div>
                    </FormControl>

                    {watch(`educationItems.${index}.qualification`) &&
                      tertiaryQualifications.includes(
                        watch(
                          `educationItems.${index}.qualification`
                        ) as EducationLevel
                      ) && (
                        <FormControl
                          isInvalid={
                            !!errors.educationItems?.[index]?.fieldOfStudy
                          }
                        >
                          <div className="flex flex-col gap-4">
                            <label
                              htmlFor={`fieldOfStudy-${index}`}
                              className="text-md lg:text-xl text-[#212121] font-medium"
                            >
                              Field of study
                            </label>
                            <Input
                              {...register(
                                `educationItems.${index}.fieldOfStudy`
                              )}
                              placeholder="e.g Electrical Engineering"
                              variant="outline"
                              rounded={0}
                              border="1px solid #D7D7D7"
                              py={{ base: 5, lg: 8 }}
                              px={{ base: 3, lg: 5 }}
                              bg="white"
                              onChange={(e) => {
                                register(
                                  `educationItems.${index}.fieldOfStudy`
                                ).onChange(e);
                                if (e.target.value.trim()) {
                                  debouncedLogEvent(
                                    "education_fieldOfStudy_changed",
                                    {
                                      jobAdId: jobId,
                                      userId: user?.id,
                                    }
                                  );
                                }
                              }}
                            />
                            <FormErrorMessage>
                              {
                                errors.educationItems?.[index]?.fieldOfStudy
                                  ?.message
                              }
                            </FormErrorMessage>
                          </div>
                        </FormControl>
                      )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
          {/* Employment History Section */}
          <Box py={8} borderBottom="1px" borderColor="#E7E7E7">
            <HStack justify="space-between" mb={6}>
              <Heading
                as="h2"
                fontSize={{ base: "xl", lg: "2xl" }}
                fontWeight="medium"
                color="#121212"
              >
                Employment history
              </Heading>
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                size="md"
                variant="outline"
                rounded={0}
                colorScheme="prussianBlue"
                onClick={() => {
                  appendEmployment({
                    companyName: "",
                    jobTitle: "",
                    description: "",
                    startedAt: "",
                    endedAt: "",
                    media: [],
                  });
                  logEvent("employment_added", {
                    jobAdId: jobId,
                    userId: user?.id,
                  });
                }}
              >
                Add
              </Button>
            </HStack>

            <Stack spacing={6}>
              {employmentFields.map((field, index) => (
                <Box key={field.id} p={6} border="1px" borderColor="#C9C9C9">
                  <HStack justify="space-between" mb={4}>
                    <Text fontWeight="medium" fontSize="lg">
                      Employment {index + 1}
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => {
                        removeEmployment(index);
                        logEvent("employment_removed", {
                          jobAdId: jobId,
                          userId: user?.id,
                        });
                      }}
                      leftIcon={<Trash2 className="h-4 w-4" />}
                    >
                      Remove
                    </Button>
                  </HStack>

                  <Stack spacing={4}>
                    <HStack spacing={4}>
                      <FormControl
                        isInvalid={
                          !!errors.employmentHistoryItems?.[index]?.companyName
                        }
                      >
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor={`companyName-${index}`}
                            className="text-md lg:text-xl text-[#212121] font-medium"
                          >
                            Company name
                          </label>
                          <Input
                            {...register(
                              `employmentHistoryItems.${index}.companyName`
                            )}
                            placeholder="LaborHack"
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            bg="white"
                            onChange={(e) => {
                              register(
                                `employmentHistoryItems.${index}.companyName`
                              ).onChange(e);
                              if (e.target.value.trim()) {
                                debouncedLogEvent(
                                  "employment_companyName_changed",
                                  {
                                    jobAdId: jobId,
                                    userId: user?.id,
                                  }
                                );
                              }
                            }}
                          />
                          <FormErrorMessage>
                            {
                              errors.employmentHistoryItems?.[index]
                                ?.companyName?.message
                            }
                          </FormErrorMessage>
                        </div>
                      </FormControl>

                      <FormControl
                        isInvalid={
                          !!errors.employmentHistoryItems?.[index]?.jobTitle
                        }
                      >
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor={`jobTitle-${index}`}
                            className="text-md lg:text-xl text-[#212121] font-medium"
                          >
                            Job title
                          </label>
                          <Input
                            {...register(
                              `employmentHistoryItems.${index}.jobTitle`
                            )}
                            placeholder="e.g Site Engineer"
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            bg="white"
                            onChange={(e) => {
                              register(
                                `employmentHistoryItems.${index}.jobTitle`
                              ).onChange(e);
                              if (e.target.value.trim()) {
                                debouncedLogEvent(
                                  "employment_jobTitle_changed",
                                  {
                                    jobAdId: jobId,
                                    userId: user?.id,
                                  }
                                );
                              }
                            }}
                          />
                          <FormErrorMessage>
                            {
                              errors.employmentHistoryItems?.[index]?.jobTitle
                                ?.message
                            }
                          </FormErrorMessage>
                        </div>
                      </FormControl>
                    </HStack>

                    <FormControl
                      isInvalid={
                        !!errors.employmentHistoryItems?.[index]?.description
                      }
                    >
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor={`jobDescription-${index}`}
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          Job description
                        </label>
                        <Textarea
                          {...register(
                            `employmentHistoryItems.${index}.description`
                          )}
                          placeholder="Describe your role and responsibilities"
                          rows={3}
                          rounded={0}
                          border="1px solid #D7D7D7"
                          py={{ base: 5, lg: 8 }}
                          px={{ base: 3, lg: 5 }}
                          bg="white"
                          onChange={(e) => {
                            register(
                              `employmentHistoryItems.${index}.description`
                            ).onChange(e);
                            if (e.target.value.trim()) {
                              debouncedLogEvent(
                                "employment_description_changed",
                                {
                                  jobAdId: jobId,
                                  userId: user?.id,
                                }
                              );
                            }
                          }}
                        />
                        <FormErrorMessage>
                          {
                            errors.employmentHistoryItems?.[index]?.description
                              ?.message
                          }
                        </FormErrorMessage>
                      </div>
                    </FormControl>

                    <HStack spacing={4}>
                      <FormControl
                        isInvalid={
                          !!errors.employmentHistoryItems?.[index]?.startedAt
                        }
                      >
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor={`startDate-${index}`}
                            className="text-md lg:text-xl text-[#212121] font-medium"
                          >
                            Start date
                          </label>
                          <Input
                            type="date"
                            {...register(
                              `employmentHistoryItems.${index}.startedAt`
                            )}
                            onChange={(e) => {
                              register(
                                `employmentHistoryItems.${index}.startedAt`
                              ).onChange(e);
                              if (e.target.value) {
                                debouncedLogEvent(
                                  "employment_startDate_changed",
                                  {
                                    jobAdId: jobId,
                                    userId: user?.id,
                                  }
                                );
                              }
                            }}
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            bg="white"
                          />
                          <FormErrorMessage>
                            {
                              errors.employmentHistoryItems?.[index]?.startedAt
                                ?.message
                            }
                          </FormErrorMessage>
                        </div>
                      </FormControl>

                      <FormControl>
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor={`endDate-${index}`}
                            className="text-md lg:text-xl text-[#212121] font-medium"
                          >
                            End date (optional)
                          </label>
                          <Input
                            type="date"
                            {...register(
                              `employmentHistoryItems.${index}.endedAt`
                            )}
                            onChange={(e) => {
                              register(
                                `employmentHistoryItems.${index}.endedAt`
                              ).onChange(e);
                              if (e.target.value) {
                                debouncedLogEvent(
                                  "employment_endDate_changed",
                                  {
                                    jobAdId: jobId,
                                    userId: user?.id,
                                  }
                                );
                              }
                            }}
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            bg="white"
                          />
                        </div>
                      </FormControl>
                    </HStack>

                    <FormControl>
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor={`images-${index}`}
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          Images (optional)
                        </label>
                        <Controller
                          name={`employmentHistoryItems.${index}.media`}
                          control={control}
                          render={({ field }) => (
                            <MediaUpload
                              media={field.value || []}
                              onMediaChange={(e) => {
                                field.onChange(e);
                                if (e.length > 0) {
                                  debouncedLogEvent(
                                    "employment_images_changed",
                                    {
                                      jobAdId: jobId,
                                      userId: user?.id,
                                    }
                                  );
                                }
                              }}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                      </div>
                    </FormControl>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
          {/* Project History Section */}
          <Box py={8} borderBottom="1px" borderColor="#E7E7E7">
            <HStack justify="space-between" mb={6}>
              <Heading
                as="h2"
                fontSize={{ base: "xl", lg: "2xl" }}
                fontWeight="medium"
                color="#121212"
              >
                Project history
              </Heading>
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                size="md"
                variant="outline"
                rounded={0}
                colorScheme="prussianBlue"
                onClick={() => {
                  appendProject({
                    name: "",
                    description: "",
                    startedAt: "",
                    endedAt: "",
                    media: [],
                  });
                  logEvent("project_added", {
                    jobAdId: jobId,
                    userId: user?.id,
                  });
                }}
              >
                Add
              </Button>
            </HStack>

            <Stack spacing={6}>
              {projectFields.map((field, index) => (
                <Box key={field.id} p={6} border="1px" borderColor="#C9C9C9">
                  <HStack justify="space-between" mb={4}>
                    <Text fontWeight="medium" fontSize="lg">
                      Project {index + 1}
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => {
                        removeProject(index);
                        logEvent("project_removed", {
                          jobAdId: jobId,
                          userId: user?.id,
                        });
                      }}
                      leftIcon={<Trash2 className="h-4 w-4" />}
                    >
                      Remove
                    </Button>
                  </HStack>

                  <Stack spacing={4}>
                    <FormControl
                      isInvalid={!!errors.projectHistoryItems?.[index]?.name}
                    >
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor={`projectName-${index}`}
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          Project name
                        </label>
                        <Input
                          {...register(`projectHistoryItems.${index}.name`)}
                          placeholder="e.g 4 bedroom apartment solar installation"
                          variant="outline"
                          rounded={0}
                          border="1px solid #D7D7D7"
                          py={{ base: 5, lg: 8 }}
                          px={{ base: 3, lg: 5 }}
                          bg="white"
                          onChange={(e) => {
                            register(
                              `projectHistoryItems.${index}.name`
                            ).onChange(e);
                            if (e.target.value.trim()) {
                              debouncedLogEvent("project_name_changed", {
                                jobAdId: jobId,
                                userId: user?.id,
                              });
                            }
                          }}
                        />
                        <FormErrorMessage>
                          {errors.projectHistoryItems?.[index]?.name?.message}
                        </FormErrorMessage>
                      </div>
                    </FormControl>

                    <FormControl
                      isInvalid={
                        !!errors.projectHistoryItems?.[index]?.description
                      }
                    >
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor={`projectDescription-${index}`}
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          Project description
                        </label>
                        <Textarea
                          {...register(
                            `projectHistoryItems.${index}.description`
                          )}
                          placeholder="Describe the project and your role"
                          rows={3}
                          rounded={0}
                          border="1px solid #D7D7D7"
                          py={{ base: 5, lg: 8 }}
                          px={{ base: 3, lg: 5 }}
                          bg="white"
                          onChange={(e) => {
                            register(
                              `projectHistoryItems.${index}.description`
                            ).onChange(e);
                            if (e.target.value.trim()) {
                              debouncedLogEvent("project_description_changed", {
                                jobAdId: jobId,
                                userId: user?.id,
                              });
                            }
                          }}
                        />
                        <FormErrorMessage>
                          {
                            errors.projectHistoryItems?.[index]?.description
                              ?.message
                          }
                        </FormErrorMessage>
                      </div>
                    </FormControl>

                    <HStack spacing={4}>
                      <FormControl>
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor={`projectStartDate-${index}`}
                            className="text-md lg:text-xl text-[#212121] font-medium"
                          >
                            Start date (optional)
                          </label>
                          <Input
                            type="date"
                            {...register(
                              `projectHistoryItems.${index}.startedAt`
                            )}
                            onChange={(e) => {
                              register(
                                `projectHistoryItems.${index}.startedAt`
                              ).onChange(e);
                              if (e.target.value) {
                                debouncedLogEvent("project_startDate_changed", {
                                  jobAdId: jobId,
                                  userId: user?.id,
                                });
                              }
                            }}
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            bg="white"
                          />
                        </div>
                      </FormControl>

                      <FormControl
                        isInvalid={
                          !!errors.projectHistoryItems?.[index]?.endedAt
                        }
                      >
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor={`projectEndDate-${index}`}
                            className="text-md lg:text-xl text-[#212121] font-medium"
                          >
                            End date
                          </label>
                          <Input
                            type="date"
                            {...register(
                              `projectHistoryItems.${index}.endedAt`
                            )}
                            onChange={(e) => {
                              register(
                                `projectHistoryItems.${index}.endedAt`
                              ).onChange(e);
                              if (e.target.value) {
                                debouncedLogEvent("project_endDate_changed", {
                                  jobAdId: jobId,
                                  userId: user?.id,
                                });
                              }
                            }}
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            bg="white"
                          />
                          <FormErrorMessage>
                            {
                              errors.projectHistoryItems?.[index]?.endedAt
                                ?.message
                            }
                          </FormErrorMessage>
                        </div>
                      </FormControl>
                    </HStack>

                    <FormControl>
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor={`projectImages-${index}`}
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          Images (optional)
                        </label>
                        <Controller
                          name={`projectHistoryItems.${index}.media`}
                          control={control}
                          render={({ field }) => (
                            <MediaUpload
                              media={field.value || []}
                              onMediaChange={(e) => {
                                field.onChange(e);
                                if (e.length > 0) {
                                  debouncedLogEvent("project_images_changed", {
                                    jobAdId: jobId,
                                    userId: user?.id,
                                  });
                                }
                              }}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                      </div>
                    </FormControl>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
          {/* Certifications Section */}
          <Box py={8} borderBottom="1px" borderColor="#E7E7E7">
            <HStack justify="space-between" mb={6}>
              <Heading
                as="h2"
                fontSize={{ base: "xl", lg: "2xl" }}
                fontWeight="medium"
                color="#121212"
              >
                Certifications
              </Heading>
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                size="md"
                variant="outline"
                rounded={0}
                colorScheme="prussianBlue"
                onClick={() => {
                  appendCertification({
                    name: "",
                    issuerCertificationId: "",
                    issuedBy: "",
                    issuedAt: "",
                    url: "",
                  });
                  logEvent("certification_added", {
                    jobAdId: jobId,
                    userId: user?.id,
                  });
                }}
              >
                Add
              </Button>
            </HStack>

            <Stack spacing={6}>
              {certificationFields.map((field, index) => (
                <Box key={field.id} p={6} border="1px" borderColor="#C9C9C9">
                  <HStack justify="space-between" mb={4}>
                    <Text fontWeight="medium" fontSize="lg">
                      Certification {index + 1}
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => {
                        removeCertification(index);
                        logEvent("certification_removed", {
                          jobAdId: jobId,
                          userId: user?.id,
                        });
                      }}
                      leftIcon={<Trash2 className="h-4 w-4" />}
                    >
                      Remove
                    </Button>
                  </HStack>

                  <Stack spacing={4}>
                    <FormControl
                      isInvalid={!!errors.certificationItems?.[index]?.name}
                    >
                      <div className="flex flex-col gap-4">
                        <label
                          htmlFor={`certificationName-${index}`}
                          className="text-md lg:text-xl text-[#212121] font-medium"
                        >
                          Certification name
                        </label>
                        <Input
                          {...register(`certificationItems.${index}.name`)}
                          placeholder="e.g UK ECITB Health and Safety"
                          variant="outline"
                          rounded={0}
                          border="1px solid #D7D7D7"
                          py={{ base: 5, lg: 8 }}
                          px={{ base: 3, lg: 5 }}
                          bg="white"
                          onChange={(e) => {
                            register(
                              `certificationItems.${index}.name`
                            ).onChange(e);
                            if (e.target.value.trim()) {
                              debouncedLogEvent("certification_name_changed", {
                                jobAdId: jobId,
                                userId: user?.id,
                              });
                            }
                          }}
                        />
                        <FormErrorMessage>
                          {errors.certificationItems?.[index]?.name?.message}
                        </FormErrorMessage>
                      </div>
                    </FormControl>

                    <HStack spacing={4}>
                      <FormControl
                        isInvalid={
                          !!errors.certificationItems?.[index]?.issuedBy
                        }
                      >
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor={`issuedBy-${index}`}
                            className="text-md lg:text-xl text-[#212121] font-medium"
                          >
                            Issued by
                          </label>
                          <Input
                            {...register(
                              `certificationItems.${index}.issuedBy`
                            )}
                            placeholder="e.g UK ECITB"
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            bg="white"
                            onChange={(e) => {
                              register(
                                `certificationItems.${index}.issuedBy`
                              ).onChange(e);
                              if (e.target.value.trim()) {
                                debouncedLogEvent(
                                  "certification_issuedBy_changed",
                                  {
                                    jobAdId: jobId,
                                    userId: user?.id,
                                  }
                                );
                              }
                            }}
                          />
                          <FormErrorMessage>
                            {
                              errors.certificationItems?.[index]?.issuedBy
                                ?.message
                            }
                          </FormErrorMessage>
                        </div>
                      </FormControl>

                      <FormControl
                        isInvalid={
                          !!errors.certificationItems?.[index]?.issuedAt
                        }
                      >
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor={`issuedAt-${index}`}
                            className="text-md lg:text-xl text-[#212121] font-medium"
                          >
                            Issue date
                          </label>
                          <Input
                            type="date"
                            {...register(
                              `certificationItems.${index}.issuedAt`
                            )}
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            bg="white"
                          />
                          <FormErrorMessage>
                            {
                              errors.certificationItems?.[index]?.issuedAt
                                ?.message
                            }
                          </FormErrorMessage>
                        </div>
                      </FormControl>
                    </HStack>

                    <HStack spacing={4}>
                      <FormControl>
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor={`certificationId-${index}`}
                            className="text-md lg:text-xl text-[#212121] font-medium"
                          >
                            Certification ID (optional)
                          </label>
                          <Input
                            {...register(
                              `certificationItems.${index}.issuerCertificationId`
                            )}
                            placeholder="e.g 1234567890"
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            bg="white"
                          />
                        </div>
                      </FormControl>

                      <FormControl>
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor={`certificationUrl-${index}`}
                            className="text-md lg:text-xl text-[#212121] font-medium"
                          >
                            Certificate URL (optional)
                          </label>
                          <Input
                            type="url"
                            {...register(`certificationItems.${index}.url`)}
                            placeholder="Enter certificate URL"
                            variant="outline"
                            rounded={0}
                            border="1px solid #D7D7D7"
                            py={{ base: 5, lg: 8 }}
                            px={{ base: 3, lg: 5 }}
                            bg="white"
                          />
                        </div>
                      </FormControl>
                    </HStack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Form Actions */}
          <Box
            py={{ base: 4, lg: 6 }}
            borderTop="1px"
            borderColor="#E7E7E7"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              variant="outline"
              onClick={() => {
                onCancel();
                logEvent("application_cancelled", {
                  jobAdId: jobId,
                  userId: user?.id,
                });
              }}
              isDisabled={isSubmitting}
              rounded={0}
              px={8}
              py={6}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="flatteredFlamingo"
              isLoading={isSubmitting}
              loadingText="Submitting application..."
              isDisabled={isSubmitting}
              rounded={0}
              px={8}
              py={6}
            >
              Submit application
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
